import JSZip from 'jszip';
import { CONTENT_TYPE, FILE_NAME } from './constants';
import { type Inspection } from './types';

class SPInspection {
  private _inspection?: Inspection.Inspection;

  public get inspection() { return this._inspection!; }

  private constructor(private deserializedSPInspectionFile?: JSZip) { }

  public static loadAsync = async (url: string) => {
    try {
      const response = await fetch(url);
      URL.revokeObjectURL(url);

      const _deserializedSPInspectionFile = await new JSZip().loadAsync(await response.blob());

      const spinspection = new SPInspection(_deserializedSPInspectionFile);
      await spinspection.setInspection();

      return spinspection;

    } catch (error) {
      throw new Error(`Failed to load SPInspection: ${error}`);
    }
  };

  public static transformInspectionForCompatibility(inspectionData: Inspection.Inspection) {
    if (inspectionData.notes != null) {
      inspectionData.pointOfInterests = inspectionData.notes;
      delete inspectionData.notes;
    }

    for (const pointOfInterest of inspectionData.pointOfInterests ?? []) {
      if (pointOfInterest.status != null || pointOfInterest.noticeType != null) {
        pointOfInterest.stepIndex ??= 0;

        pointOfInterest.steps = [{
          noticeType: pointOfInterest.noticeType ?? pointOfInterest.status,
          description: pointOfInterest.description,
          comment: pointOfInterest.comment ?? pointOfInterest.message,
          drawings: pointOfInterest.drawings,
          measurements: pointOfInterest.measurements,
          resources: pointOfInterest.resources ?? pointOfInterest.documents,
          constraints: pointOfInterest.constraints,
          metadata: pointOfInterest.metadata,
          dateCreated: pointOfInterest.dateCreated,
          dateModified: pointOfInterest.dateModified
        }] as Inspection.Step[];

        delete pointOfInterest.description;
        delete pointOfInterest.message;
        delete pointOfInterest.noticeType;
        delete pointOfInterest.status;
        delete pointOfInterest.drawings;
        delete pointOfInterest.measurements;
        delete pointOfInterest.resources;
        delete pointOfInterest.constraints;
        delete pointOfInterest.dateCreated;
        delete pointOfInterest.dateModified;
      }

      for (const step of pointOfInterest?.steps ?? []) {
        let noticeType = (step.noticeType ?? step.status ?? 'NotChecked') as Inspection.Status;

        if (noticeType === 'Default' || noticeType === 'None') noticeType = 'NotChecked';
        else if (noticeType === 'Alert') noticeType = 'Warning';

        step.noticeType = noticeType;
        delete step.status;

        step.comment ??= step.message ?? '';
        delete step.message;
      }
    }

    // Inspection-level attachments were renamed `documents` -> `resources`. Both names
    // are live: the key is stored verbatim on upload, so old inspections keep `documents`.
    // Only rewrite when the old key is actually present — this also runs on API responses,
    // where an absent key means "not requested", not "empty".
    if (inspectionData.documents != null) {
      inspectionData.resources ??= inspectionData.documents;
      delete inspectionData.documents;
    }
  };

  // An inspection-level image, which is what the SuPAR app and the PDF report both count as a
  // snapshot. A camera pose is not part of the test: most snapshots carry one, but nothing here
  // reads it, and requiring it would hide images the report still lists.
  public static isSnapshot = (resource: { type: Inspection.ResourceType }): boolean =>
    resource.type === 'Image';

  public static getPOIStatus = (poi: Inspection.PointOfInterest): Inspection.NoticeType => {
    const steps = poi.steps;

    if (steps.some(step => step.noticeType === 'Error')) return 'Error';
    if (steps.some(step => step.noticeType === 'Warning')) return 'Warning';
    if (steps.some(step => step.noticeType === 'NotChecked')) return 'NotChecked';
    if (steps.every(step => step.noticeType === 'Ok')) return 'Ok';

    return 'NotChecked';
  };

  private setInspection = async (): Promise<void> => {
    const file = this.deserializedSPInspectionFile?.file(FILE_NAME.INSPECTION);
    if (file == null) throw new Error(`Could not find ${FILE_NAME.INSPECTION} in the inspection.`);

    const JSONString = await file.async('string');
    const inspectionData = JSON.parse(JSONString) as Inspection.Inspection;

    SPInspection.transformInspectionForCompatibility(inspectionData);

    this._inspection = inspectionData;
  };

  public getResourcesByPoiId = async (noteId: string, index = 0): Promise<Inspection.NoteResource[]> => {
    const poi = this._inspection?.pointOfInterests?.find(poi => poi.id == noteId);
    if (poi == null) throw new Error('Could not find note.');

    const resources = poi?.steps?.[index]?.resources ?? [];
    const noteResources: Inspection.NoteResource[] = [];

    for (const resource of resources) {
      const file = this.deserializedSPInspectionFile?.file(`${resource.id}.${resource.name}`);

      const blob = file ? new Blob([await file.async('blob')], { type: CONTENT_TYPE[resource.type] }) : null;
      const resourceUrl = blob ? (URL || webkitURL).createObjectURL(blob) : '';

      noteResources.push({ ...resource, resourceUrl });
    }

    return noteResources;
  };

  public getResources = async (
    filter?: (resource: Inspection.Document) => boolean
  ): Promise<Inspection.NoteResource[]> => {
    const resources = this._inspection?.resources ?? [];
    const noteResources: Inspection.NoteResource[] = [];

    for (const resource of resources) {
      if (filter && !filter(resource)) continue;

      const file = this.deserializedSPInspectionFile?.file(`${resource.id}.${resource.name}`);

      const blob = file ? new Blob([await file.async('blob')], { type: CONTENT_TYPE[resource.type] }) : null;
      const resourceUrl = blob ? (URL || webkitURL).createObjectURL(blob) : '';

      noteResources.push({ ...resource, resourceUrl });
    }

    return noteResources;
  };

  public cleanup = () => {
    delete this.deserializedSPInspectionFile;
    delete this._inspection;
  };
}

export default SPInspection;
